import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { createTeam } from '../../services/api/team.service';
import { toast } from 'react-toastify';
import { ArrowLeft, Save } from 'lucide-react';
import { useArtist } from '../../contexts/ArtistContext';
import { trackEvent, trackActivationMilestone, TEAM_EVENTS, ACTIVATION_EVENTS } from '../../services/analytics';
import { auth } from '../../config/firebase';

const CreateTeam: React.FC = () => {
  const navigate = useNavigate();
  const { availableArtists, currentArtistId } = useArtist();
  const [formData, setFormData] = useState({
    teamName: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);

  // Check if user is owner of the SELECTED artist (not just any artist)
  const selectedArtist = availableArtists.find(artist => artist.artistId === currentArtistId);
  // Only allow if user is explicitly the owner (isOwner === true)
  // Team members will have isOwner === false or undefined, so they cannot create teams
  // System admins are also excluded - only the artist owner can create teams
  const isOwnerOfSelectedArtist = selectedArtist?.isOwner === true;
  
  // Only the artist (owner) can create teams, not team members or system admins
  // Team members (isOwner === false) will be redirected to dashboard
  // Allow access only if user is owner of the selected artist (selectedArtist exists AND isOwner === true)
  const hasAccess = selectedArtist !== undefined && isOwnerOfSelectedArtist;

  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.teamName.trim()) {
      toast.error('Team Name is required');
      return;
    }

    try {
      setLoading(true);
      const response = await createTeam({
        teamName: formData.teamName.trim(),
        description: formData.description.trim() || undefined,
      });

      // Track team created
      trackEvent(TEAM_EVENTS.TEAM_CREATED);

      toast.success('Team created successfully');
      navigate(`/teams/${response.teamId}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to create team');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-nova-dashboard-900 min-h-screen">
      <div className="p-4 sm:p-6 md:p-8">
        {/* Header */}
        <div className="flex items-center gap-4 mt-[3rem] mb-6">
          <button
            onClick={() => navigate('/teams')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-700" />
          </button>
          <h1 className="text-[1.875rem] font-regular text-gray-900 font-family-messina-modern">
            Create New Team
          </h1>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 font-family-messina-sans">
                Team Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.teamName}
                onChange={(e) => setFormData({ ...formData, teamName: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-900 font-family-messina-sans"
                placeholder="Enter team name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 font-family-messina-sans">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-900 font-family-messina-sans"
                placeholder="Enter team description (optional)"
                rows={4}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => navigate('/teams')}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`px-4 py-2 rounded-lg text-white text-sm font-family-messina-sans flex items-center gap-2 ${
                  loading
                    ? 'bg-gray-500 cursor-not-allowed'
                    : 'bg-gray-800 hover:bg-gray-700'
                }`}
              >
                <Save size={16} />
                <span>{loading ? 'Creating...' : 'Create Team'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateTeam;

